import React, { useState, useEffect } from 'react';
import { Star, MessageSquare } from 'lucide-react';
import { Button } from './button';
import RatingForm from './RatingForm';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';

const RateClubButton = ({ clubId, clubName, onRatingSubmitted }) => {
  const { user } = useAuth();
  const [showRatingForm, setShowRatingForm] = useState(false);
  const [canRate, setCanRate] = useState(false);
  const [existingRating, setExistingRating] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkRatingEligibility();
  }, [clubId, user]);

  const checkRatingEligibility = async () => {
    if (!user || !clubId) {
      setLoading(false);
      return;
    }

    // Only runners can rate clubs
    if (user.user_type !== 'runner') {
      setCanRate(false);
      setLoading(false);
      return;
    }

    try {
      // Check if user can rate this club
      const eligibilityResponse = await apiService.clubRatings.canRateClub(clubId);
      setCanRate(eligibilityResponse.can_rate);

      // If user has already rated, get their existing rating
      if (eligibilityResponse.can_rate === false && eligibilityResponse.can_update === true) {
        const myRating = await apiService.clubRatings.getMyClubRating(clubId);
        setExistingRating(myRating);
      }
    } catch (error) {
      console.error('Error checking rating eligibility:', error);
      setCanRate(false);
    } finally {
      setLoading(false);
    }
  };

  const handleRateClick = () => {
    if (!user) {
      // Redirect to login if not authenticated
      window.location.href = '/auth';
      return;
    }
    setShowRatingForm(true);
  };

  const handleRatingSubmitted = () => {
    setShowRatingForm(false);
    setExistingRating(null);
    checkRatingEligibility(); // Refresh eligibility
    onRatingSubmitted?.(); // Notify parent component
  };

  if (loading) {
    return null; // Don't show anything while loading
  }

  if (!canRate && !existingRating) {
    return null; // Don't show button if user can't rate
  }


  return (
    <>
      <Button
        onClick={handleRateClick}
        className="bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-2 rounded-full font-semibold text-sm transition-colors shadow-lg"
        style={{fontFamily: 'Montserrat, sans-serif'}}
      >
        <Star className="w-4 h-4 mr-2" />
        {existingRating ? 'Update Review' : 'Rate Club'}
      </Button>

      {showRatingForm && (
        <RatingForm
          clubId={clubId}
          clubName={clubName}
          existingRating={existingRating}
          onSuccess={handleRatingSubmitted}
          onCancel={() => setShowRatingForm(false)}
          isOpen={showRatingForm}
        />
      )}
    </>
  );
};

export default RateClubButton;
